import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: October 2, 2025</p>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700">
                By accessing or using FutureLens ("the Service"), you agree to be bound by these Terms of Service
                ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
              <p className="text-gray-700">
                FutureLens provides AI-powered career intelligence and analysis services, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Career insights and personalized reports</li>
                <li>Skills assessment and gap analysis</li>
                <li>Market compensation data and trends</li>
                <li>Career path recommendations</li>
                <li>Professional profile analysis</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700">
                To use certain features of the Service, you must create an account. You agree to provide accurate,
                current, and complete information during registration and to update such information to keep it
                accurate, current, and complete.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Account Security</h3>
              <p className="text-gray-700">
                You are responsible for maintaining the confidentiality of your account credentials and for all
                activities that occur under your account. You agree to notify us immediately of any unauthorized
                access or use of your account.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Account Termination</h3>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your account at any time for violation of these Terms
                or for any other reason at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. User Responsibilities</h2>
              <p className="text-gray-700">You agree to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Use the Service only for lawful purposes</li>
                <li>Provide accurate and truthful information</li>
                <li>Not impersonate any person or entity</li>
                <li>Not interfere with or disrupt the Service</li>
                <li>Not attempt to gain unauthorized access to any portion of the Service</li>
                <li>Not use the Service to transmit any viruses, malware, or harmful code</li>
                <li>Not violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Content and Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Your Content</h3>
              <p className="text-gray-700">
                You retain ownership of any content you submit to the Service, including resumes, career information,
                and other materials ("User Content"). By submitting User Content, you grant us a worldwide,
                non-exclusive, royalty-free license to use, reproduce, and process your content solely for the
                purpose of providing and improving the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Our Content</h3>
              <p className="text-gray-700">
                The Service and all materials therein, including but not limited to software, text, graphics, logos,
                and reports generated by our AI, are owned by FutureLens or our licensors and are protected by
                intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.3 Generated Reports</h3>
              <p className="text-gray-700">
                Reports and insights generated through our AI analysis are provided to you for your personal,
                non-commercial use. You may not resell, redistribute, or use these reports for commercial purposes
                without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Subscription and Payment</h2>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.1 Subscription Plans</h3>
              <p className="text-gray-700">
                Some features of the Service require a paid subscription. Subscription fees are described on our
                pricing page and are subject to change with notice.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.2 Billing</h3>
              <p className="text-gray-700">
                By subscribing to a paid plan, you authorize us to charge your payment method on a recurring basis.
                You are responsible for all charges incurred under your account.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.3 Refunds</h3>
              <p className="text-gray-700">
                Refund policies are described on our pricing page. Unless otherwise stated, subscription fees are
                non-refundable.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.4 Cancellation</h3>
              <p className="text-gray-700">
                You may cancel your subscription at any time. Cancellation will be effective at the end of your
                current billing period.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Disclaimers and Limitations</h2>
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.1 No Warranty</h3>
              <p className="text-gray-700">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
                OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.2 No Career Guarantees</h3>
              <p className="text-gray-700">
                FutureLens provides career insights and recommendations based on AI analysis, but we do not guarantee
                any specific career outcomes, job placements, salary increases, or other results.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.3 Limitation of Liability</h3>
              <p className="text-gray-700">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FUTURELENS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
                DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless FutureLens and its officers, directors, employees, and
                agents from any claims, damages, losses, liabilities, and expenses arising out of your use of the
                Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Modifications to the Service</h2>
              <p className="text-gray-700">
                We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any
                time with or without notice. We shall not be liable to you or any third party for any modification,
                suspension, or discontinuance of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Modifications to Terms</h2>
              <p className="text-gray-700">
                We may modify these Terms at any time by posting the revised Terms on the Service. Your continued
                use of the Service after such modifications constitutes your acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of the State of Missouri,
                without regard to its conflict of law provisions. Any disputes arising from these Terms or your use
                of the Service shall be resolved in the courts located in St. Louis County, Missouri.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
                shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact Information</h2>
              <p className="text-gray-700">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mt-4">
                <p className="text-gray-700">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:connect@optivusbusinesssolutions.com" className="text-primary hover:underline">
                    connect@optivusbusinesssolutions.com
                  </a>
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Phone:</strong>{' '}
                  <a href="tel:314-514-5125" className="text-primary hover:underline">
                    314-514-5125
                  </a>
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Address:</strong><br />
                  1200 Lake Saint Louis Blvd, Unit #325<br />
                  Lake Saint Louis, MO 63367
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
